import { CheckCircle, Edit, Plus } from 'lucide-react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsAdManagement,
  type GoogleAdsAdManagementResult,
} from 'sprites-types/workflow/tools/googleAdsAdManagement'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleAdsAdManagementDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsAdManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsAdManagementResult
  const isCreate = 'adId' in result && result.adId !== null
  const resourceName = 'resourceName' in result ? result.resourceName : undefined
  const message = 'message' in result ? result.message : undefined
  const adId = 'adId' in result ? result.adId : undefined

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3">
        <div className="mt-1 flex-shrink-0">
          <div className="rounded-full bg-purple-100 p-2">
            {isCreate ? (
              <Plus className="h-5 w-5 text-purple-600" />
            ) : (
              <Edit className="h-5 w-5 text-purple-600" />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <SiGoogleads className="h-4 w-4 text-[#4285f4]" />
            <div className="text-sm font-semibold text-gray-900">
              {isCreate ? 'Ad Created' : 'Ad Updated'}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
            {adId && <div className="mb-1 text-xs text-gray-500">Ad ID: {adId}</div>}
            {resourceName && (
              <div className="mb-1 text-xs text-gray-500">
                Resource: {resourceName.split('/').pop()}
              </div>
            )}
            {message && <div className="text-xs text-gray-700">{message}</div>}
          </div>

          {isCreate && (
            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span>Ad is ready to serve</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadGoogleAdsAdManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsAdManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsAdManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleAdsAdManagementResult
  const isCreate = 'adId' in result && result.adId !== null
  const adId = 'adId' in result ? result.adId : undefined
  const resourceName = 'resourceName' in result ? result.resourceName : undefined
  const message = 'message' in result ? result.message : undefined

  return `## Google Ads Ad ${isCreate ? 'Created' : 'Updated'}

${adId ? `**Ad ID:** ${adId}` : ''}
${resourceName ? `**Resource:** ${resourceName}` : ''}
${message ? `**Message:** ${message}` : ''}

---
`
}

export const GoogleAdsAdManagementDisplay = createWorkflowToolComponent(
  GoogleAdsAdManagementDisplayComponent,
  downloadGoogleAdsAdManagement,
  googleAdsAdManagementToMarkdown
)
